import { useCallback } from "react";
import backend from "../backend";
import { MarkerDot, SquiggleItem } from "../types/types";
import { useOpenItemsContext } from "../context/OpenItemsContext";
import { useAdventureDataContext } from "../context/AdventureDataContext";
import { systemItems } from "../biz/systemItems";

export const useImageGlue = (
  adventureId: string,
  refreshAll: () => void,
  imageDialogState: { item: SquiggleItem, imageIndex: number}|undefined,
  setImageDialogState: React.Dispatch<React.SetStateAction<{ item: SquiggleItem, imageIndex: number } | undefined>>
) => {

  const { reconstituteOpenItems, addOpenItem, setSelectedItemId } = useOpenItemsContext();
  const { reloadItems } = useAdventureDataContext();

  const uploadImage = useCallback(async (imageUploadFile: File, itemId: string) => {
    const imageUploadResult = await backend.uploadImage(adventureId, itemId, imageUploadFile);
    if (!imageUploadResult.ok) {
      console.log("Failed to upload image");
      if (imageUploadResult.status === 403) {
        return <span>Image number limit reached. Request a higher limit. <span onClick={(e) => { addOpenItem(systemItems[7]); setSelectedItemId(systemItems[7].id); e.stopPropagation(); }} style={{cursor: 'pointer', textDecoration: 'underline'}}>Open info</span>.</span>;
      } else if (imageUploadResult.status === 400) {
        return <span>Something went wrong when uploading the image. The image may be too large or not in an accepted format (PNG, JPEG, GIF, WEBP, SVG, AVIF).</span>;
      } else {
        return <span>Failed to upload image due to an unknown error. Please try again.</span>;
      }
    }
    refreshAll();
    return null;
  }, [adventureId, refreshAll, addOpenItem, setSelectedItemId]);

  const removeImage = useCallback(async (itemId: string, imageIndex: number) => {
    const imageUploadResult = await backend.removeImage(adventureId, itemId, imageIndex);
    if (!imageUploadResult.ok) {
      console.log("Failed to remove image");
      return;
    }
    refreshAll();
  }, [adventureId, refreshAll]);

  const addMarker = useCallback((itemId: string, imageIndex: number, toItemId: string|undefined, name: string, x: number, y: number, dot: MarkerDot) => {
    (async () => {
      if (!imageDialogState) {
        throw new Error("Unexpected state - expected image dialog state to be filled.")
      }
      if (toItemId) {
        const unlinkResponse = await backend.unlink(adventureId, itemId, toItemId);
        if (!unlinkResponse.ok) {
          return;
        }

        const linkResponse = await backend.link(adventureId, itemId, toItemId);
        if (!linkResponse.ok) {
          return;
        }
      }
      
      const setCoordResponse = await backend.addMarker(adventureId, itemId, imageIndex, toItemId, name, x, y, dot);
      if (!setCoordResponse.ok) {
        return;
      }
      const items = await reloadItems();
      const newAllItems = reconstituteOpenItems(items.quests, items.peopleAndOrgs, items.places, items.misc, items.logEntries, systemItems);
      const updatedImageItem = newAllItems.find(i => i.id === imageDialogState.item.id);
      setImageDialogState({ item: updatedImageItem!, imageIndex: imageDialogState.imageIndex });
    })();
  }, [adventureId, reloadItems, imageDialogState, setImageDialogState, reconstituteOpenItems]);

  const removeMarker = useCallback((itemId: string, imageIndex: number, markerId: string) => {
    (async () => {
      if (!imageDialogState) {
        throw new Error("Unexpected state - expected image dialog state to be filled.")
      }
      const linkResponse = await backend.removeMarker(adventureId, itemId, imageIndex, markerId);
      if (!linkResponse.ok) {
        return;
      }
      const items = await reloadItems();
      const newAllItems = reconstituteOpenItems(items.quests, items.peopleAndOrgs, items.places, items.misc, items.logEntries, systemItems);
      const updatedImageItem = newAllItems.find(i => i.id === imageDialogState.item.id);
      setImageDialogState({ item: updatedImageItem!, imageIndex: imageDialogState.imageIndex });
    })();
  }, [adventureId, reloadItems, imageDialogState, setImageDialogState, reconstituteOpenItems]);

  return { addMarker, removeMarker, uploadImage, removeImage };
};